<template>
  <PageHeader :showBreadcrumb="true" />
  <p>
    Please refer to the Guidelines outlined in
    <a href="https://www.health.state.mn.us/facilities/insurance/managedcare/networkadequacy/docs/netadequacyinst.pdf">Network Adequacy Submission Instructions (PDF)</a>
    for submitting these uploads. File templates are available here: <a href="https://www.health.state.mn.us/facilities/insurance/managedcare/networkadequacy/filingreqprovidernetwork.html">Filing Provider Network</a>
  </p>
  <p>
    <b>Network Adequacy attestation: </b>
    Confirms health plans meet regulatory requirements for their provider networks and that all information supplied is accurate. Only use the latest template for this form to create the Network Adequacy Attestation.
    Please note, the due date for filing Network Adequacy form is May 22nd through the MCS portal.  (Note: MDH staff must be able to change the due date for every year.) Late filings will be subject to a fine up to $500 for each day, the filing is past due. You need to submit separate Network Adequacy Attestation form(s) for each network.
  </p>
  <p>
    <b>Provider File: </b>
    Complete listing of all providers and facilities in the health plans network.
  </p>
  <p>
    <b>Geographic access maps: </b>
    Visual demonstration for the networks ability to meet distance/time requirements outlined in statutes. Multiple maps that include all MDH designated primary care, specialty, and facility types.
    Include all providers listed in the provider file. Any gaps require waiver documentation. For plan year 2025 complete maps using the lesser of travel distance/time instead of radius. Please use current census data and not enrollee data.
  </p>
  <p>
    <b>Network Waiver: </b>
    Required for each specialty (broken down by county) that doesn't meet regulatory requirements in Minnesota Statute, chapter 62K.10 subd. 2 and 3.
  </p>
  <p>
    <b>ECP Waiver: </b>
    Required if health plan network doesn't meet the 35% Essential Community Providers (ECP) in their network and/or their missing one of each ECP provider type.
    It is not necessary to request a waiver in situations where there is no ECP of that category available in the county identified in either the state or federal ECP list. Late filings will be subject to a fine up to $500 for each day, the filing is past due.
  </p>
  <hr/>
  <div class="container p-3 text-center">
    <!-- Submit form to include entity and network info -->
    <form @submit.stop.prevent="onSubmit">
      <!-- Display the user entities -->
      <div class="row mb-2 g-3">
        <div class="col-lg-12 col-md-12">
          <FormSelectGroup v-if="userEntityAssigned"
                           label="Select Entity"
                           inputId="entity-input"
                           :required="true"
                           state>
            <template #default="">
              <PvSelect id="entity-input"
                        v-model="selectedEntity"
                        :options="userEntityOptions"
                        @change="onChangeEntity"
                        ariaLabel="entity-input-label"
                        placeholder="-- Select Entity --"
                        optionLabel="name"
                        optionValue="id"/>
            </template>
          </FormSelectGroup>
        </div>
      </div>
      <!-- Display the Network list and ID -->
      <p v-if="selectedEntity && selectedNetwork" class="fw-bold">
        Note: Please select another network from the drop-down, if you like to upload form(s) for that network.
      </p>
      <div class="row mb-2 g-3">
        <div class="col-lg-12 col-md-12">
          <FormSelectGroup v-if="selectedEntity"
                           label="Select Network"
                           inputId="network-input"
                           :required="true"
                           state>
            <template #default="">
              <PvSelect id="network-input"
                        v-model="selectedNetwork"
                        :options="userNetworkOptions"
                        @change="onChangeNetwork"
                        ariaLabel="network-input-label"
                        placeholder="-- Select Network --"
                        :optionLabel="formatNetworkLabel"
                        optionValue="id"/>
            </template>
          </FormSelectGroup>
        </div>
        <!-- Add a new Network -->
        <div v-if="isAddingNetwork" class="text-start mt-2">
          <label for="new-network-name" class="form-label">New Network Name</label>
          <InputText id="new-network-name"
                     type="text"
                     v-model="formState.state.data.newNetworkName"
                     class="form-control mb-2"
                     placeholder="Enter network name" />

          <label for="new-network-id" class="form-label">New Network ID</label>
          <InputText id="new-network-id"
                     type="text"
                     v-model="formState.state.data.newNetworkId"
                     class="form-control mb-2"
                     placeholder="Enter network ID" />

          <label class="form-label d-block mb-1">Network Type</label>
          <div class="form-check mb-2">
            <RadioButton inputId="smallGroup"
                         name="networkType"
                         value="Small Group"
                         v-model="formState.state.data.newMarketType" />
            <label for="smallGroup" class="ml-2">Small Group</label>
          </div>
          <div class="form-check mb-3">
            <RadioButton inputId="individual"
                         name="networkType"
                         value="Individual"
                         v-model="formState.state.data.newMarketType" />
            <label for="individualMarket" class="ml-2">Individual</label>
          </div>
          <div class="form-check mb-4">
            <RadioButton inputId="dental"
                         name="networkType"
                         value="Dental"
                         v-model="formState.state.data.newMarketType" />
            <label for="individualMarket" class="ml-2">Dental</label>
          </div>
          <Button type="submit" class="btn btn-primary" label="Add Network" />
        </div>
      </div>
    </form>
    <!-- Display the username(email) and fullName -->
    <div v-if="selectedEntity && selectedNetwork" class="row mb-2 g-3">
      <div v-if="!isAddingNetwork" class="col-lg-4 col-md-12">
        <ReadOnlyText label="Network ID" :value="appStore.networks.find(n => n.id === selectedNetwork).networkId" />
      </div>
      <div class="col-lg-4 col-md-12">
        <ReadOnlyText v-if="store.user" label="Full Name" :value="store.user.firstName + ' ' + store.user.lastName" />
      </div>
      <div class="col-lg-4 col-md-12">
        <ReadOnlyText v-if="store.user" label="Email" :value="store.user.email" />
      </div>
    </div>
    <hr/>
    <!-- Documents Upload/Delete Section -->
    <div v-if="selectedEntity && selectedNetwork && !isAddingNetwork" class="row">
      <div class="col-lg-6 col-md-12">
        <div class="border portal-box bg-light shadow-sm rounded p-4 mb-2">
          <MNFileUpload module-type="Annual Network Review" label="Network Adequacy Attestation Upload" ref="networkAdequacyAttestation" name="networkAdequacyAttestation" accept="application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document" url="/undefined" :entity="selectedEntity" :network="selectedNetwork"/>
        </div>
      </div>
      <div class="col-lg-6 col-md-12">
        <div class="border portal-box bg-light shadow-sm rounded p-4 mb-2">
          <MNFileUpload module-type="Annual Network Review"  label="Provider File Upload" ref="providerFile" name="providerFile" accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel.sheet.macroEnabled.12" url="/undefined" :entity="selectedEntity" :network="selectedNetwork"/>
        </div>
      </div>
      <div class="col-lg-6 col-md-12">
        <div class="border portal-box bg-light shadow-sm rounded p-4 mb-2">
          <MNFileUpload module-type="Annual Network Review" label="Geographic Access Map Upload" ref="geographicAccessMap" name="geographicAccessMap" accept="application/pdf" url="/undefined" :entity="selectedEntity" :network="selectedNetwork"/>
        </div>
      </div>
      <div class="col-lg-6 col-md-12">
        <div class="border portal-box bg-light shadow-sm rounded p-4 mb-2">
          <MNFileUpload module-type="Annual Network Review"  label="Network Waiver Upload" ref="networkWaiver" name="networkWaiver" accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel.sheet.macroEnabled.12" url="/undefined" :entity="selectedEntity" :network="selectedNetwork"/>
        </div>
      </div>
      <div class="col-lg-6 col-md-12">
        <div class="border portal-box bg-light shadow-sm rounded p-4 mb-2">
          <MNFileUpload module-type="Annual Network Review"  label="ECP Waiver Upload" ref="ecpWaiver" name="ecpWaiver" accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel.sheet.macroEnabled.12" url="/undefined" :entity="selectedEntity" :network="selectedNetwork"/>
        </div>
      </div>
      <div class="col-lg-6 col-md-12">
        <div class="border portal-box bg-light shadow-sm rounded p-4 mb-2">
          <MNFileUpload module-type="Annual Network Review" label="Other File Upload" ref="otherFile" name="otherFile" accept="application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/pdf" url="/undefined" :entity="selectedEntity" :network="selectedNetwork"/>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import PageHeader from "@/components/common/PageHeader.vue";
import MNFileUpload from "@/components/common/MNFileUpload.vue";
import FormSelectGroup from "@/components/common/FormSelectGroup.vue";
import {useAppStore} from "@/stores/appStore.js";
import {ENTITY_TYPES} from "@/utils/constants.js";
import {computed, ref, nextTick} from "vue";
import {useAuthStore} from "@/stores/authStore.js";
import ReadOnlyText from "@/components/common/ReadOnlyText.vue";
import Button from 'primevue/button';
import InputText from 'primevue/inputtext';
import RadioButton from 'primevue/radiobutton';
import {useForm} from "@/composables/form.js";
import {RegisterNetworkModel} from "@/models/RegisterNetworkModel.js";

const store = useAuthStore();
const appStore = useAppStore();
const selectedEntity = ref();
const selectedNetwork = ref();

const networkAdequacyAttestation = ref();
const providerFile = ref();
const geographicAccessMap = ref();
const networkWaiver = ref();
const ecpWaiver = ref();
const otherFile = ref();

const isAddingNetwork = ref(false);

const formState = useForm(new RegisterNetworkModel(), RegisterNetworkModel.getValidationRules());

const userEntityOptions = computed(() => {
  return appStore.getUserEntitiesByTypes([ENTITY_TYPES.HMO,ENTITY_TYPES.InsuranceCompany]);
});

const userNetworkOptions = computed(() => {
  const base = appStore.getNetworksByEntityId(selectedEntity.value) || [];
  return [
    ...base,
    { id: 'add-new', networkName: 'Not In the list', networkId: '' }
  ];
});

const userEntityAssigned = computed(() => {
  return userEntityOptions.value && userEntityOptions.value.length > 0;
});

const onChangeEntity = async ({originalEvent, value}) => {
  selectedNetwork.value = null;
  formState.state.data.entityId = value;
  await clearUploaders();
};

const onChangeNetwork = async ({originalEvent, value}) => {
  if (value === 'add-new') {
    isAddingNetwork.value = true;
    formState.state.data.newNetworkName = '';
    formState.state.data.newNetworkId = '';
    formState.state.data.newMarketType = '';
  }
  else {
    console.log('Selected Network: ', selectedNetwork.value);
    isAddingNetwork.value = false;
    await clearUploaders();
  }
};

const formatNetworkLabel = (option) => {
  if (!option) return '';
  return `${option.networkName} (${option.networkId})`;
};

const clearUploaders = async () => {
  networkAdequacyAttestation.value?.clear();
  providerFile.value?.clear();
  geographicAccessMap.value?.clear();
  networkWaiver.value?.clear();
  ecpWaiver.value?.clear();
  otherFile.value?.clear();
};

const onSubmit = async () => {
  formState.state.$v.$touch();
  if (formState.state.$v.$invalid) {
    console.log('formState state is invalid!');
    nextTick(() => formState.focusInvalidInput());
  } else {
    // Save the registration
    console.log('formState data is ready to be saved: ', formState.state.data);
    await formState.save(() => formState.state.data.save());
  }
};
</script>

<style scoped>

</style>